import { useState, useEffect, useContext } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, ArrowRight, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../../App";

const OnboardingAgreement = () => {
  const navigate = useNavigate();
  const { refreshData } = useOutletContext();
  const { refreshUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [agreementText, setAgreementText] = useState("");
  const [agreementVersion, setAgreementVersion] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    fetchAgreement();
  }, []);

  const fetchAgreement = async () => {
    try {
      // Get current version info
      const versionRes = await axios.get(`${API}/legal/photographer-agreement/current-version`);
      const version = versionRes.data.version;
      setAgreementVersion(version);

      // Get agreement text
      const textRes = await axios.get(`${API}/legal/photographer-agreement/${version}`);
      setAgreementText(textRes.data.content);
    } catch (error) {
      toast.error("Failed to load agreement");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    const scrolledToBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleContinue = async () => {
    if (!agreed) {
      toast.error("Please agree to the Photographer Platform Agreement to continue");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Accept the agreement
      await axios.post(
        `${API}/legal/photographer-agreement/accept`,
        { version: agreementVersion },
        { headers }
      );

      // Update onboarding step
      await axios.put(
        `${API}/onboarding/step`,
        { step: "PROFILE_CONTENT" },
        { headers }
      );

      toast.success("Agreement accepted!");
      
      // Refresh both onboarding data AND auth user data
      await refreshData();
      if (refreshUser) await refreshUser();
      
      navigate("/dashboard/onboarding/profile");
    } catch (error) {
      console.error("Agreement error:", error);
      toast.error(error.response?.data?.detail || "Failed to save agreement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid="onboarding-agreement"
    >
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-serif">Photographer Platform Agreement</CardTitle>
              <CardDescription>
                Please review and accept the agreement to continue
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Agreement Version Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Version {agreementVersion}</span>
            <span>•</span>
            <span>Effective upon acceptance</span>
          </div>

          {/* Agreement Text */}
          <div className="border border-border rounded-lg bg-muted/20">
            <ScrollArea 
              className="h-[400px] p-6"
              onScrollCapture={handleScroll}
            >
              <div className="prose prose-invert prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                  {agreementText}
                </pre>
              </div>
            </ScrollArea>
          </div>

          {/* Scroll hint */}
          {!hasScrolledToBottom && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span>Please scroll to read the entire agreement</span>
            </div>
          )}

          {/* Checkbox Agreement */}
          <div className="flex items-start space-x-3 p-4 bg-muted/30 rounded-lg border border-border">
            <Checkbox
              id="agreement"
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-1"
              data-testid="agreement-checkbox"
            />
            <div className="space-y-1">
              <Label
                htmlFor="agreement"
                className="text-base font-medium cursor-pointer"
              >
                I agree to the Photographer Platform Agreement
              </Label>
              <p className="text-sm text-muted-foreground">
                By checking this box, you acknowledge that you have read, understood, and agree to be bound by the terms of this agreement.
              </p>
            </div>
          </div>

          {/* Continue Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleContinue}
              disabled={!agreed || saving}
              className="btn-primary"
              data-testid="agreement-continue-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default OnboardingAgreement;
